import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { informesAPI, fondosAPI } from '../services/api';
import { toast } from 'react-toastify';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import '../Clientes.css';
import '../ProyeccionFondos.css';

const formatMoney = (n) =>
  `$${(parseFloat(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const OPCIONES_INCLUIR = [
  { id: 'saldos', label: 'Saldo actual', hint: 'Opcional: ninguna caja/cuenta = $0' },
  { id: 'abonos', label: 'Abonos mensuales', hint: 'Clientes activos' },
  { id: 'cheques', label: 'Cheques cartera', hint: 'Ingresos por cobro' },
  { id: 'cheques_emitidos', label: 'Cheques emitidos', hint: 'Ninguna cuenta = sin salidas' },
  { id: 'sueldos', label: 'Sueldos + aguinaldo', hint: '50% jun y dic' },
  { id: 'gastos_fijos', label: 'Gastos fijos', hint: 'Tipos proveedor' },
  { id: 'gastos_registrados', label: 'Gastos registrados', hint: 'Módulo gastos (prom. 3 meses)' },
];

function EntidadMultiselect({ label, items, selected, onToggle, onSelectAll, onClear, renderLabel }) {
  return (
    <div className="proyeccion-entidad-block">
      <label className="block-label">
        <span>{label}</span>
        <span className="block-actions">
          <button type="button" onClick={onSelectAll}>Todas</button>
          <button type="button" onClick={onClear}>Ninguna</button>
        </span>
      </label>
      <div className="proyeccion-multiselect">
        {items.length === 0 ? (
          <span style={{ color: '#999', fontSize: '12px' }}>Sin registros activos</span>
        ) : (
          items.map((item) => (
            <label key={item.id} className="proyeccion-multiselect-option">
              <input
                type="checkbox"
                checked={selected.includes(String(item.id))}
                onChange={() => onToggle(item.id)}
              />
              <span>{renderLabel(item)}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

const ProyeccionFondos = () => {
  const [horizonte, setHorizonte] = useState(6);
  const [incluir, setIncluir] = useState(OPCIONES_INCLUIR.map((o) => o.id));
  const [cajas, setCajas] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [cajasSeleccionadas, setCajasSeleccionadas] = useState([]);
  const [cuentasSeleccionadas, setCuentasSeleccionadas] = useState([]);
  const [cuentasChequesEmitidos, setCuentasChequesEmitidos] = useState([]);
  const [entidadesCargadas, setEntidadesCargadas] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tipoGrafico, setTipoGrafico] = useState('linea');

  useEffect(() => {
    fondosAPI.getEntidades({ activo: true })
      .then((res) => {
        if (!res.success) return;
        const entidades = res.data || [];
        const c = entidades.filter((e) => e.tipo_entidad === 'caja');
        const cb = entidades.filter((e) => e.tipo_entidad === 'cuenta');
        setCajas(c);
        setCuentas(cb);
        const idsCajas = c.map((x) => String(x.id));
        const idsCuentas = cb.map((x) => String(x.id));
        setCajasSeleccionadas(idsCajas);
        setCuentasSeleccionadas(idsCuentas);
        setCuentasChequesEmitidos(idsCuentas);
        setEntidadesCargadas(true);
      })
      .catch(() => toast.error('No se pudieron cargar cajas y cuentas'));
  }, []);

  const cargar = useCallback(async () => {
    if (incluir.length === 0) {
      toast.warning('Seleccione al menos un concepto a incluir');
      return;
    }
    try {
      setLoading(true);
      const res = await informesAPI.proyeccionFondos({
        horizonte_meses: horizonte,
        incluir,
        caja_ids: cajasSeleccionadas,
        cuenta_bancaria_ids: cuentasSeleccionadas,
        cuentas_cheques_emitidos_ids: cuentasChequesEmitidos,
      });
      if (res.success) {
        setData(res.data);
      } else {
        toast.error(res.message || 'Error al calcular');
      }
    } catch (e) {
      toast.error(e.message || 'Error al calcular proyección');
    } finally {
      setLoading(false);
    }
  }, [horizonte, incluir, cajasSeleccionadas, cuentasSeleccionadas, cuentasChequesEmitidos]);

  useEffect(() => {
    if (entidadesCargadas) cargar();
  }, [entidadesCargadas, cargar]);

  const chartData = useMemo(() => {
    if (!data?.meses) return [];
    return data.meses.map((m) => ({
      name: m.etiqueta,
      entradas: m.entradas.total,
      salidas: m.salidas.total,
      saldo: m.saldo_final,
    }));
  }, [data]);

  const toggleIncluir = (id) => {
    setIncluir((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleId = (id, setter) => {
    const s = String(id);
    setter((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const saldoFinal = parseFloat(data?.proyeccion_saldo_final) || 0;
  const saldoActual = parseFloat(data?.saldo_actual_fondos) || 0;
  const variacion = saldoFinal - saldoActual;

  const kpis = useMemo(() => {
    if (!data) return [];
    const items = [
      { key: 'actual', label: 'Saldo actual', value: saldoActual, highlight: true },
      { key: 'final', label: 'Proyección final', value: saldoFinal, highlight: true, negative: saldoFinal < 0 },
      { key: 'var', label: 'Variación', value: variacion, negative: variacion < 0, positive: variacion > 0 },
    ];
    if (incluir.includes('abonos')) {
      items.push({ key: 'abonos', label: 'Abonos / mes', value: data.supuestos?.abonos_mensuales?.total });
    }
    if (incluir.includes('sueldos')) {
      items.push({ key: 'sueldos', label: 'Sueldos / mes', value: data.supuestos?.sueldos_mensuales?.total });
    }
    if (incluir.includes('gastos_fijos')) {
      items.push({ key: 'gf', label: 'Gastos fijos / mes', value: data.supuestos?.gastos_fijos?.total_mensual });
    }
    if (incluir.includes('gastos_registrados')) {
      items.push({ key: 'gr', label: 'Gastos registrados / mes', value: data.supuestos?.gastos_registrados?.total_mensual });
    }
    if (incluir.includes('cheques_emitidos')) {
      items.push({ key: 'che', label: 'Ch. emitidos', value: data.supuestos?.cheques_emitidos_total });
    }
    return items;
  }, [data, incluir, saldoActual, saldoFinal, variacion]);

  return (
    <div className="clientes proyeccion-fondos">
      <div className="page-header">
        <h1>Proyección de flujo de fondos</h1>
        <p className="page-subtitle">Estimación mensual — no reemplaza contabilidad real</p>
      </div>

      <div className="proyeccion-layout">
        <aside className="proyeccion-panel">
          <div className="proyeccion-panel__head">
            <h2>Parámetros</h2>
          </div>
          <div className="proyeccion-panel__body">
            <div className="proyeccion-toolbar">
              <div className="proyeccion-field">
                <label htmlFor="pf-horizonte">Horizonte</label>
                <select id="pf-horizonte" value={horizonte} onChange={(e) => setHorizonte(Number(e.target.value))}>
                  {[3, 6, 9, 12, 18, 24].map((n) => (
                    <option key={n} value={n}>{n} meses</option>
                  ))}
                </select>
              </div>
              <div className="proyeccion-field">
                <label htmlFor="pf-grafico">Gráfico</label>
                <select id="pf-grafico" value={tipoGrafico} onChange={(e) => setTipoGrafico(e.target.value)}>
                  <option value="linea">Saldo (línea)</option>
                  <option value="barras">Entradas / salidas</option>
                </select>
              </div>
              <button type="button" className="btn-primary" onClick={cargar} disabled={loading}>
                {loading ? '…' : 'Recalcular'}
              </button>
            </div>

            <div className="proyeccion-section">
              <p className="proyeccion-section__title">Conceptos</p>
              <div className="proyeccion-chips">
                {OPCIONES_INCLUIR.map((op) => {
                  const active = incluir.includes(op.id);
                  return (
                    <label
                      key={op.id}
                      className={`proyeccion-chip${active ? ' proyeccion-chip--active' : ''}`}
                    >
                      <input type="checkbox" checked={active} onChange={() => toggleIncluir(op.id)} />
                      <span>
                        <strong>{op.label}</strong>
                        <br />
                        <span style={{ color: '#888' }}>{op.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="proyeccion-section">
              <p className="proyeccion-section__title">Fondos y cuentas</p>
              <div className="proyeccion-entidades-grid">
                <EntidadMultiselect
                  label="Cajas (saldo)"
                  items={cajas}
                  selected={cajasSeleccionadas}
                  onToggle={(id) => toggleId(id, setCajasSeleccionadas)}
                  onSelectAll={() => setCajasSeleccionadas(cajas.map((c) => String(c.id)))}
                  onClear={() => setCajasSeleccionadas([])}
                  renderLabel={(c) => c.nombre}
                />
                <EntidadMultiselect
                  label="Cuentas (saldo)"
                  items={cuentas}
                  selected={cuentasSeleccionadas}
                  onToggle={(id) => toggleId(id, setCuentasSeleccionadas)}
                  onSelectAll={() => setCuentasSeleccionadas(cuentas.map((c) => String(c.id)))}
                  onClear={() => setCuentasSeleccionadas([])}
                  renderLabel={(cb) => `${cb.banco_nombre || 'Banco'} — ${cb.numero_cuenta || cb.nombre}`}
                />
                <EntidadMultiselect
                  label="Cuentas (ch. emitidos)"
                  items={cuentas}
                  selected={cuentasChequesEmitidos}
                  onToggle={(id) => toggleId(id, setCuentasChequesEmitidos)}
                  onSelectAll={() => setCuentasChequesEmitidos(cuentas.map((c) => String(c.id)))}
                  onClear={() => setCuentasChequesEmitidos([])}
                  renderLabel={(cb) => `${cb.banco_nombre || 'Banco'} — ${cb.numero_cuenta || cb.nombre}`}
                />
              </div>
            </div>
          </div>
        </aside>

        <div className="proyeccion-main">
          {loading && !data && (
            <div className="proyeccion-loading">Calculando proyección…</div>
          )}

          {data && (
            <>
              <div className="proyeccion-kpis">
                {kpis.map((k) => (
                  <div
                    key={k.key}
                    className={`proyeccion-kpi${k.highlight ? ' proyeccion-kpi--highlight' : ''}${k.negative ? ' proyeccion-kpi--negative' : ''}`}
                  >
                    <span className="proyeccion-kpi__label">{k.label}</span>
                    <span className={`proyeccion-kpi__value${k.positive ? ' positive' : ''}${k.negative ? ' negative' : ''}`}>
                      {k.key === 'var' && k.value > 0 ? '+' : ''}{formatMoney(k.value)}
                    </span>
                  </div>
                ))}
              </div>

              <p className="proyeccion-disclaimer">{data.advertencia}</p>

              <div className="proyeccion-results-grid">
                <div className="proyeccion-card">
                  <div className="proyeccion-card__head">Evolución del saldo</div>
                  <div className="proyeccion-card__body">
                    {chartData.length === 0 ? (
                      <div className="proyeccion-empty">Sin datos para graficar</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        {tipoGrafico === 'barras' ? (
                          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis tickFormatter={(v) => formatMoney(v)} width={72} tick={{ fontSize: 10 }} />
                            <Tooltip formatter={(v) => formatMoney(v)} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="entradas" name="Entradas" fill="#27ae60" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="salidas" name="Salidas" fill="#e74c3c" radius={[2, 2, 0, 0]} />
                          </BarChart>
                        ) : (
                          <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis tickFormatter={(v) => formatMoney(v)} width={72} tick={{ fontSize: 10 }} />
                            <Tooltip formatter={(v) => formatMoney(v)} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="saldo" name="Saldo" stroke="#2980b9" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        )}
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                <div className="proyeccion-card">
                  <div className="proyeccion-card__head">Detalle mensual</div>
                  <div className="proyeccion-card__body proyeccion-card__body--table">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Mes</th>
                          <th>Inicial</th>
                          <th>Ent.</th>
                          <th>Sal.</th>
                          <th>Final</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.meses.map((m) => (
                          <tr key={m.periodo}>
                            <td><strong>{m.etiqueta}</strong></td>
                            <td>{formatMoney(m.saldo_inicial)}</td>
                            <td style={{ color: '#27ae60' }}>{formatMoney(m.entradas.total)}</td>
                            <td style={{ color: '#c0392b' }}>{formatMoney(m.salidas.total)}</td>
                            <td style={{ fontWeight: 600, color: m.saldo_final < 0 ? '#e74c3c' : '#2c3e50' }}>
                              {formatMoney(m.saldo_final)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {incluir.includes('gastos_fijos') && data.supuestos?.gastos_fijos?.detalle?.length > 0 && (
                <div className="proyeccion-card proyeccion-gastos-fijos">
                  <div className="proyeccion-card__head">Gastos fijos por tipo proveedor</div>
                  <div className="proyeccion-card__body proyeccion-card__body--table" style={{ maxHeight: 'none' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Promedio / mes</th>
                          <th>Últ. 3 meses</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.supuestos.gastos_fijos.detalle.map((g) => (
                          <tr key={g.tipo_proveedor_id}>
                            <td>{g.tipo_proveedor}</td>
                            <td>{formatMoney(g.promedio_mensual)}</td>
                            <td>{formatMoney(g.egresos_ultimos_3_meses)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {!loading && !data && entidadesCargadas && (
            <div className="proyeccion-empty">Configure los parámetros y pulse Recalcular</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProyeccionFondos;
